<script setup>
const props = defineProps({
  heading: {
    type: String,
  },
  items: {
    type: Array,
  },
  topic: {
    type: String,
  },
})

const MAX_VISIBLE = 5

// Only render categories that actually have a name — guards against malformed /
// empty items rendering as nameless links/buttons (axe link-name/button-name).
const validItems = computed(() => (props.items ?? []).filter(item => item?.articleCategoryName))

const visibleItems = computed(() => validItems.value.slice(0, MAX_VISIBLE))

const hiddenItems = computed(() => validItems.value.slice(MAX_VISIBLE))

const dropdownItems = computed(() => [
  hiddenItems.value.map(item => ({
    label: item.articleCategoryName,
    to: item.articleCategoryUrl,
  })),
])
</script>

<!-- Topic Filter -->
<template>
  <nav
    class="filter-topic mb-15"
    aria-label="Filter articles by topic"
  >
    <!-- Mobile Dropdown Filter -->
    <UCollapsible
      class="flex flex-col w-full card-shadow rounded-xl xl:hidden"
    >
      <UButton
        class="group data-[state=open]:rounded-b-none bg-med-light-aqua text-med-deep-navy justify-between"
        trailing-icon="i-lucide-funnel"
      >
        {{ props.heading }}
      </UButton>
      <template #content>
        <div class="filter-dropdown__body">
          <ul class="filter-dropdown__list flex flex-col gap-3.5">
            <li
              v-for="item in validItems"
              :key="item.articleCategoryName"
              class="filter-dropdown__item"
              :class="{ active: item.activeCategory }"
            >
              <ULink
                :to="useCmsUrl(item.articleCategoryUrl)"
                :locale="false"
              >
                {{ item.articleCategoryName }}
              </ULink>
            </li>
          </ul>
        </div>
      </template>
    </UCollapsible>
    <!-- Desktop Filter -->
    <div class="hidden xl:block xl:border-b-med-light-aqua xl:border-b 3xl:max-w-block-wide m-auto">
      <h2
        v-if="props.heading"
        class="text-base font-semibold xl:text-lg xl:font-bold"
      >
        {{ props.heading }}
      </h2>
      <div class="flex items-center filter-dropdown__list">
        <!-- Visible Categories -->
        <ul class="flex items-center gap-7.5 text-center">
          <li
            v-for="item in visibleItems"
            :key="item.articleCategoryName"
            class="filter-dropdown__item"
            :class="{ active: item.activeCategory }"
          >
            <ULink
              :to="useCmsUrl(item.articleCategoryUrl)"
              :locale="false"
              class="text-med-dark-gray"
            >
              {{ item.articleCategoryName }}
            </ULink>
          </li>
        </ul>

        <div class="ml-auto">
          <UDropdownMenu
            v-if="hiddenItems.length"
            :items="dropdownItems"
          >
            <template #default>
              <UButton
                variant="ghost"
                trailing-icon="i-lucide-chevron-down"
              >
                More
              </UButton>
            </template>
            <template #content>
              <ul class="h-75 overflow-y-auto flex flex-col">
                <li
                  v-for="item in hiddenItems"
                  :key="item.articleCategoryName"
                  class="py-2 px-4 hover:bg-med-light-aqua"
                >
                  <a :href="item.articleCategoryUrl">
                    {{ item.articleCategoryName }}
                  </a>
                </li>
              </ul>
            </template>
          </UDropdownMenu>
        </div>
      </div>
    </div>
  </nav>
</template>

<style lang="scss">
@use '@/assets/css/blocks/topic-filter-block.scss';
</style>
